/**
 * Supplier Management Dialog
 * Allows adding new suppliers or selecting existing ones from email context
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { supplierService, Supplier as SupplierType } from '../../../services/supplierService';

interface Supplier {
  id: string;
  name: string;
  company_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  created_at?: string;
  updated_at?: string;
}

interface EmailContext {
  email_id: string;
  from_address: string;
  subject?: string;
  body_text?: string;
}

interface SupplierManagementDialogProps {
  open: boolean;
  onClose: () => void;
  emailContext: EmailContext;
  onSupplierSelected?: (supplier: SupplierType) => void;
}

const SupplierManagementDialog: React.FC<SupplierManagementDialogProps> = ({
  open,
  onClose,
  emailContext,
  onSupplierSelected
}) => {
  const { showNotification } = useNotification();
  
  // State
  const [activeTab, setActiveTab] = useState<'search' | 'add' | 'edit'>('search');
  const [suppliers, setSuppliers] = useState<SupplierType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierType | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<SupplierType | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<SupplierType | null>(null);
  
  // New supplier form state
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    company_name: '',
    email: emailContext.from_address,
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });

  // Edit supplier form state
  const [editSupplier, setEditSupplier] = useState({
    id: '',
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });

  // Load suppliers on open
  useEffect(() => {
    if (open) {
      loadSuppliers();
      // Pre-fill new supplier email from email context
      setNewSupplier(prev => ({
        ...prev,
        email: emailContext.from_address
      }));
    }
  }, [open, emailContext.from_address]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      console.log('[SupplierManagement] Loading suppliers...');
      const response = await supplierService.getSuppliers();
      if (response.success) {
        console.log('[SupplierManagement] Loaded suppliers:', response.data?.length || 0);
        console.log('[SupplierManagement] Suppliers with null emails:', response.data?.filter(s => !s.email).length || 0);
        setSuppliers(response.data || []);
      } else {
        console.error('[SupplierManagement] Failed to load suppliers:', response.message);
        showNotification('Fehler beim Laden der Lieferanten: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('[SupplierManagement] Error loading suppliers:', error);
      showNotification('Fehler beim Laden der Lieferanten: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierSearch = (query: string) => {
    setSearchQuery(query);
    // Real-time search will be implemented with debouncing in production
  };

  const handleSelectSupplier = (supplier: SupplierType) => {
    console.log('[SupplierManagement] Supplier selected:', supplier);
    setSelectedSupplier(supplier);
    if (onSupplierSelected) {
      console.log('[SupplierManagement] Calling onSupplierSelected callback');
      onSupplierSelected(supplier);
    }
    showNotification(`Lieferant ${supplier.name} ausgewählt`, 'success');
    onClose();
  };

  const handleAddSupplier = async () => {
    try {
      if (!newSupplier.name || !newSupplier.email) {
        showNotification('Name und E-Mail sind erforderlich', 'error');
        return;
      }

      setLoading(true);
      const response = await supplierService.createSupplier(newSupplier);
      
      if (response.success) {
        showNotification('Neuer Lieferant erfolgreich hinzugefügt', 'success');
        
        if (onSupplierSelected && response.data) {
          onSupplierSelected(response.data);
        }
        
        onClose();
      } else {
        showNotification('Fehler beim Hinzufügen des Lieferanten: ' + response.message, 'error');
      }
    } catch (error: any) {
      showNotification('Fehler beim Hinzufügen des Lieferanten: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, supplier: SupplierType) => {
    event.stopPropagation();
    setSelectedSupplier(supplier);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditSupplier = (supplier: SupplierType) => {
    setEditingSupplier(supplier);
    setEditSupplier({
      id: supplier.id,
      name: supplier.name,
      company_name: supplier.company_name,
      email: supplier.email,
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      postal_code: supplier.postal_code || ''
    });
    setActiveTab('edit');
    handleMenuClose();
  };

  const handleDeleteConfirmOpen = (supplier: SupplierType) => {
    setSupplierToDelete(supplier);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSupplierToDelete(null);
  };

  const handleUpdateSupplier = async () => {
    try {
      if (!editSupplier.name || !editSupplier.email) {
        showNotification('Name und E-Mail sind erforderlich', 'error');
        return;
      }

      setLoading(true);
      const { id, ...updateData } = editSupplier;
      const response = await supplierService.updateSupplier(id, updateData);
      
      if (response.success) {
        showNotification('Lieferant erfolgreich aktualisiert', 'success');
        loadSuppliers(); // Refresh supplier list
        setActiveTab('search');
        setEditingSupplier(null);
      } else {
        showNotification('Fehler beim Aktualisieren des Lieferanten: ' + response.message, 'error');
      }
    } catch (error: any) {
      showNotification('Fehler beim Aktualisieren des Lieferanten: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async () => {
    if (!supplierToDelete) return;
    
    try {
      setLoading(true);
      const response = await supplierService.deleteSupplier(supplierToDelete.id);
      
      if (response.success) {
        showNotification('Lieferant erfolgreich gelöscht', 'success');
        loadSuppliers(); // Refresh supplier list
        handleDeleteConfirmClose();
      } else {
        showNotification('Fehler beim Löschen des Lieferanten: ' + response.message, 'error');
      }
    } catch (error: any) {
      showNotification('Fehler beim Löschen des Lieferanten: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesName = supplier.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCompany = supplier.company_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEmail = supplier.email && supplier.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (searchQuery && !matchesName && !matchesCompany && !matchesEmail) {
      return false;
    }
    
    return matchesName || matchesCompany || matchesEmail;
  });

  const handleClose = () => {
    setActiveTab('search');
    setSearchQuery('');
    setSelectedSupplier(null);
    setEditingSupplier(null);
    setMenuAnchorEl(null);
    setDeleteConfirmOpen(false);
    setSupplierToDelete(null);
    setNewSupplier({
      name: '',
      company_name: '',
      email: emailContext.from_address,
      phone: '',
      address: '',
      city: '',
      postal_code: ''
    });
    setEditSupplier({
      id: '',
      name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Lieferanten-Verwaltung
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          E-Mail von: {emailContext.from_address}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Tab Selection */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant={activeTab === 'search' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('search')}
            sx={{ mr: 1 }}
            startIcon={<SearchIcon />}
          >
            Lieferant suchen
          </Button>
          <Button
            variant={activeTab === 'add' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('add')}
            startIcon={<AddIcon />}
            sx={{ mr: 1 }}
          >
            Neuen Lieferant hinzufügen
          </Button>
          {activeTab === 'edit' && (
            <Button
              variant="contained"
              onClick={() => setActiveTab('search')}
              startIcon={<CancelIcon />}
            >
              Bearbeitung abbrechen
            </Button>
          )}
        </Box>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <Box>
            <TextField
              fullWidth
              label="Lieferant suchen"
              value={searchQuery}
              onChange={(e) => handleSupplierSearch(e.target.value)}
              placeholder="Name, Firma oder E-Mail eingeben..."
              sx={{ mb: 2 }}
            />
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredSuppliers.length === 0 ? (
                  <Alert severity="info">
                    Keine Lieferanten gefunden. Möchten Sie einen neuen Lieferant hinzufügen?
                  </Alert>
                ) : (
                  filteredSuppliers.map((supplier) => (
                    <ListItem key={supplier.id} disablePadding>
                      <ListItemButton onClick={() => handleSelectSupplier(supplier)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {supplier.name}
                              </Typography>
                              <Chip label={supplier.id} size="small" />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                <BusinessIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {supplier.company_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {supplier.email}
                              </Typography>
                              {supplier.phone && (
                                <Typography variant="body2" color="text.secondary">
                                  <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                  {supplier.phone}
                                </Typography>
                              )}
                              {supplier.city && (
                                <Typography variant="body2" color="text.secondary">
                                  <LocationIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                  {supplier.address}, {supplier.city} {supplier.postal_code}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        {/* Actions Menu */}
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, supplier)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Box>
        )}

        {/* Add New Supplier Tab */}
        {activeTab === 'add' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Neuen Lieferant basierend auf der E-Mail-Adresse <strong>{emailContext.from_address}</strong> hinzufügen
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nachname oder Hauptkontakt"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Firmenname *"
                  value={newSupplier.company_name}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Vollständiger Firmenname"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E-Mail *"
                  value={newSupplier.email}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, email: e.target.value }))}
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={newSupplier.phone}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={newSupplier.address}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Straße und Hausnummer (optional)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Stadt"
                  value={newSupplier.city}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postleitzahl"
                  value={newSupplier.postal_code}
                  onChange={(e) => setNewSupplier(prev => ({ ...prev, postal_code: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Edit Supplier Tab */}
        {activeTab === 'edit' && editingSupplier && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Lieferant <strong>{editingSupplier.name}</strong> bearbeiten
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  value={editSupplier.name}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nachname oder Hauptkontakt"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Firmenname *"
                  value={editSupplier.company_name}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Vollständiger Firmenname"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E-Mail *"
                  value={editSupplier.email}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, email: e.target.value }))}
                  type="email"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={editSupplier.phone}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={editSupplier.address}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Straße und Hausnummer (optional)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Stadt"
                  value={editSupplier.city}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postleitzahl"
                  value={editSupplier.postal_code}
                  onChange={(e) => setEditSupplier(prev => ({ ...prev, postal_code: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItemComponent onClick={() => selectedSupplier && handleEditSupplier(selectedSupplier)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Bearbeiten
          </MenuItemComponent>
          <MenuItemComponent onClick={() => selectedSupplier && handleDeleteConfirmOpen(selectedSupplier)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Löschen
          </MenuItemComponent>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteConfirmClose}
          aria-labelledby="delete-confirmation-dialog-title"
        >
          <DialogTitle id="delete-confirmation-dialog-title">
            Lieferant löschen
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Sind Sie sicher, dass Sie den Lieferanten <strong>{supplierToDelete?.name}</strong> löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteConfirmClose}>
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                handleDeleteSupplier();
              }}
              variant="contained"
              color="error"
            >
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Abbrechen
        </Button>
        {activeTab === 'add' && (
          <Button
            onClick={handleAddSupplier}
            variant="contained"
            disabled={loading || !newSupplier.name || !newSupplier.email}
          >
            {loading ? <CircularProgress size={20} /> : 'Lieferant hinzufügen'}
          </Button>
        )}
        {activeTab === 'edit' && (
          <Button
            onClick={handleUpdateSupplier}
            variant="contained"
            disabled={loading || !editSupplier.name || !editSupplier.email}
          >
            {loading ? <CircularProgress size={20} /> : 'Änderungen speichern'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SupplierManagementDialog;
